import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import PERMISSIONS from 'constants/permissions';
import { mutationEditorClear } from 'gql/mutations/editor';
import { useMutationQuestionMove } from 'gql/mutations/question';
import { useQueryCoursesMeChapters } from 'gql/queries/course';
import { useQueryLocalEditor } from 'gql/queries/local';
import extract from 'helpers/extract';
import { trackEvent, reportError } from 'lib/tracking';

import QuestionMove from './QuestionMove';

const QuestionMoveContainer = () => {
  const { t } = useTranslation();
  const mutationQuestionMove = useMutationQuestionMove();
  const {
    data: {
      editor: { status: editorStatus, questionEditing },
    },
  } = useQueryLocalEditor();
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        courseId: yup
          .string()
          .required(
            t('Course/Teacher/Editor/QuestionMove/course-error-required'),
          )
          .trim()
          .uuid(),
        chapterId: yup
          .string()
          .nullable()
          .transform((v) => v || null)
          .trim()
          .uuid(),
      }),
    [t],
  );
  const handleQuestionMove = useCallback(
    async (values, form) => {
      try {
        form.setStatus(null);
        const variables = extract(values, validationSchema);
        await mutationQuestionMove(questionEditing, variables);
        mutationEditorClear();
        trackEvent('question', 'move');
      } catch (err) {
        form.setStatus('500');
        reportError('question', 'move', err);
      }
    },
    [mutationQuestionMove, questionEditing, validationSchema],
  );
  const isModalOpen = editorStatus === 'question-move';
  const { isLoading, data } = useQueryCoursesMeChapters({
    skip: !isModalOpen,
  });
  const courses = isLoading
    ? []
    : data.coursesMe.filter(
        (course) => course.level >= PERMISSIONS.COLLABORATOR,
      );
  return (
    <QuestionMove
      courses={courses}
      handleClose={mutationEditorClear}
      handleQuestionMove={handleQuestionMove}
      isModalOpen={isModalOpen}
      t={t}
      validationSchema={validationSchema}
    />
  );
};

export default QuestionMoveContainer;
